/**
 * Iter52.58b · 10 · Cambio de contraseña del usuario identificado.
 *
 * Dos modos:
 *  - `forzado` (10b): primer acceso. Ocupa toda la pantalla y no se puede
 *    cerrar: el backend responde 403 en el resto de rutas hasta que se
 *    cambie la contraseña.
 *  - normal (10a): se abre desde el perfil como panel modal.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { KeyRound, ShieldCheck, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MIN = 10;
const cumpleReglas = (v) => (v || '').length >= MIN
  && /[A-Za-zÁÉÍÓÚáéíóúÑñ]/.test(v || '') && /\d/.test(v || '');

export default function CambiarPasswordPanel({ forzado = false, onClose, onDone }) {
  const [f, setF] = useState({ actual: '', nueva: '', repetir: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setError(''); };

  const valido = f.actual && cumpleReglas(f.nueva) && f.nueva === f.repetir;

  const enviar = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/auth/cambiar-password`, {
        password_actual: f.actual, password_nueva: f.nueva,
        password_repetir: f.repetir,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('mobilsafe_token')}` } });
      try {
        const u = JSON.parse(localStorage.getItem('mobilsafe_user') || '{}');
        u.debe_cambiar_password = false;
        localStorage.setItem('mobilsafe_user', JSON.stringify(u));
      } catch { /* localStorage ilegible: el backend ya es la verdad */ }
      setOk(true);
      setTimeout(() => { onDone && onDone(); }, 900);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'No se pudo cambiar la contraseña');
    } finally { setBusy(false); }
  };

  const formulario = (
    <form onSubmit={enviar} className="space-y-4" data-testid="form-cambiar-password">
      {forzado && (
        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3" data-testid="aviso-password-obligatorio">
          Por seguridad, debes cambiar la contraseña que te han asignado antes de
          entrar. Hasta entonces no podrás usar el resto de la aplicación.
        </div>
      )}
      <label className="block">
        <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Contraseña actual</span>
        <input data-testid="password-actual" type="password" autoComplete="current-password"
               className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none"
               value={f.actual} onChange={e => set('actual', e.target.value)} />
      </label>
      <label className="block">
        <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Nueva contraseña</span>
        <input data-testid="password-nueva" type="password" autoComplete="new-password"
               className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none"
               value={f.nueva} onChange={e => set('nueva', e.target.value)} />
        <span className={`text-[11px] mt-1 block ${cumpleReglas(f.nueva) || !f.nueva ? 'text-slate-500' : 'text-rose-600'}`}>
          Mínimo {MIN} caracteres, con letras y números.
        </span>
      </label>
      <label className="block">
        <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Repetir nueva contraseña</span>
        <input data-testid="password-repetir" type="password" autoComplete="new-password"
               className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none"
               value={f.repetir} onChange={e => set('repetir', e.target.value)} />
        {f.repetir && f.repetir !== f.nueva && (
          <span className="text-[11px] text-rose-600 mt-1 block">No coinciden.</span>
        )}
      </label>
      {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="password-error">{error}</p>}
      {ok && <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2" data-testid="password-ok"><ShieldCheck className="w-4 h-4" /> Contraseña actualizada.</p>}
      <div className="flex gap-2 justify-end pt-1">
        {!forzado && onClose && (
          <button type="button" onClick={onClose} disabled={busy}
                  className="text-sm px-4 py-2 rounded-xl border border-slate-300 bg-white font-semibold hover:bg-slate-50">
            Cancelar
          </button>
        )}
        <button type="submit" data-testid="password-guardar" disabled={busy || !valido || ok}
                className="text-sm px-4 py-2 rounded-xl bg-emerald-700 text-white font-bold hover:bg-emerald-800 disabled:opacity-50">
          {busy ? 'Guardando…' : 'Cambiar contraseña'}
        </button>
      </div>
    </form>
  );

  if (forzado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center px-4 py-10"
           data-testid="pantalla-password-obligatorio">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7">
          <img src="/logo-mobilsafe.png" alt="MOBILSAFE" className="h-7 mb-5" />
          <h1 className="text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-emerald-700" /> Cambia tu contraseña
          </h1>
          <p className="text-sm text-slate-500 mt-1 mb-5">Primer acceso a la plataforma.</p>
          {formulario}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center px-4"
         data-testid="modal-cambiar-password">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 relative">
        <button onClick={onClose} data-testid="cerrar-cambiar-password"
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-700">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-black tracking-tight text-slate-900 flex items-center gap-2 mb-5">
          <KeyRound className="w-5 h-5 text-emerald-700" /> Cambiar contraseña
        </h2>
        {formulario}
      </div>
    </div>
  );
}
